/* ==========================================================================
   NGA — Collection

   Facet dropdowns, AJAX filtering, sorting, grid density and load-more.

   Filtering goes through Shopify's Section Rendering API rather than a full
   page load, so the hero, tabs and header stay put and only the grid swaps.
   Every state change is written to the URL, so filtered views stay
   shareable, bookmarkable and back-button friendly.
   ========================================================================== */
(function () {
  'use strict';

  var DENSITY_KEY = 'nga:grid-density';

  function root() { return document.querySelector('[data-nga-collection]'); }

  /* ---------------------------------------------------------------------
     Dropdown open / close
     --------------------------------------------------------------------- */
  function closeAll(except) {
    document.querySelectorAll('.nga-facet.is-open, .nga-sort.is-open').forEach(function (el) {
      if (el === except) return;
      el.classList.remove('is-open');
      var t = el.querySelector('[aria-expanded]');
      if (t) t.setAttribute('aria-expanded', 'false');
    });
  }

  document.addEventListener('click', function (e) {
    var toggle = e.target.closest('[data-nga-facet-toggle], [data-nga-sort-toggle]');
    if (toggle) {
      e.preventDefault();
      var wrap = toggle.closest('.nga-facet, .nga-sort');
      var willOpen = !wrap.classList.contains('is-open');
      closeAll(wrap);
      wrap.classList.toggle('is-open', willOpen);
      toggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      return;
    }
    if (!e.target.closest('.nga-facet__panel, .nga-sort__panel')) closeAll();
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeAll();
  });

  /* ---------------------------------------------------------------------
     Fetch + swap
     --------------------------------------------------------------------- */
  var controller = null;

  function render(url, opts) {
    opts = opts || {};
    var host = root();
    if (!host) return;

    var sectionId = host.getAttribute('data-section-id');
    var wrap = host.querySelector('[data-nga-cgrid-wrap]');
    if (wrap) wrap.classList.add('is-loading');

    if (controller) controller.abort();
    controller = ('AbortController' in window) ? new AbortController() : null;

    var sep = url.indexOf('?') === -1 ? '?' : '&';
    var fetchUrl = url + sep + 'section_id=' + sectionId;

    fetch(fetchUrl, controller ? { signal: controller.signal } : undefined)
      .then(function (r) {
        if (!r.ok) throw new Error('filter failed');
        return r.text();
      })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var fresh = doc.querySelector('[data-nga-collection]');
        if (!fresh) throw new Error('section missing');

        // Swap only the parts that change.
        ['[data-nga-cfilters]', '[data-nga-ctoolbar]', '[data-nga-cgrid-wrap]'].forEach(function (sel) {
          var oldEl = host.querySelector(sel);
          var newEl = fresh.querySelector(sel);
          if (oldEl && newEl) oldEl.replaceWith(newEl);
        });

        if (!opts.replace) {
          window.history.pushState({ nga: true }, '', url);
        } else {
          window.history.replaceState({ nga: true }, '', url);
        }

        applyDensity();
        document.dispatchEvent(new CustomEvent('nga:refresh', { detail: { root: host } }));

        if (opts.scroll !== false) {
          var top = host.getBoundingClientRect().top + window.pageYOffset - 120;
          window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
        }
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        // Filtering must never strand the shopper — fall back to a real navigation.
        window.location.href = url;
      });
  }

  /* Any link inside the filter UI filters in place instead of navigating. */
  document.addEventListener('click', function (e) {
    var link = e.target.closest('[data-nga-filter-link]');
    if (!link) return;
    e.preventDefault();
    closeAll();
    render(link.href);
  });

  /* ---------------------------------------------------------------------
     Price range
     --------------------------------------------------------------------- */
  document.addEventListener('submit', function (e) {
    var form = e.target.closest('[data-nga-price-form]');
    if (!form) return;
    e.preventDefault();
    var base = form.getAttribute('data-base-url') || window.location.pathname;
    var params = new URLSearchParams(window.location.search);
    params.delete('filter.v.price.gte');
    params.delete('filter.v.price.lte');
    params.delete('page');
    var min = form.querySelector('[name="min"]');
    var max = form.querySelector('[name="max"]');
    if (min && min.value) params.set('filter.v.price.gte', min.value);
    if (max && max.value) params.set('filter.v.price.lte', max.value);
    var qs = params.toString();
    render(base + (qs ? '?' + qs : ''));
  });

  /* ---------------------------------------------------------------------
     Load more — append the next page rather than replacing the grid
     --------------------------------------------------------------------- */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-nga-loadmore]');
    if (!btn || btn.disabled) return;
    e.preventDefault();

    var host = root();
    if (!host) return;

    // The whole collection is already on the page, in-stock first and
    // sold-out last; reveal the next batch in place rather than fetching a
    // page, which would restart that ordering.
    if (btn.hasAttribute('data-nga-reveal')) {
      var step = parseInt(btn.getAttribute('data-nga-reveal'), 10) || 36;
      var held = host.querySelectorAll('[data-nga-cgrid] > li[data-nga-more]');
      for (var i = 0; i < held.length && i < step; i++) held[i].removeAttribute('data-nga-more');
      var shownEl = host.querySelector('[data-nga-shown]');
      var meter = host.querySelector('[data-nga-meter]');
      var visible = host.querySelectorAll('[data-nga-cgrid] > li:not([data-nga-more]):not([data-nga-grid-card])').length;
      if (shownEl) shownEl.textContent = visible;
      if (meter) meter.style.width = Math.min(100, Math.round(visible * 100 / (parseInt(meter.getAttribute('data-total'), 10) || visible))) + '%';
      if (held.length <= step) {
        btn.removeAttribute('data-nga-reveal');
        if (!btn.getAttribute('data-next-url')) btn.remove();
      }
      document.dispatchEvent(new CustomEvent('nga:refresh', { detail: { root: host.querySelector('[data-nga-cgrid]') } }));
      return;
    }

    var sectionId = host.getAttribute('data-section-id');
    var nextUrl = btn.getAttribute('data-next-url');
    if (!nextUrl) return;

    var label = btn.textContent;
    btn.disabled = true;
    btn.textContent = btn.getAttribute('data-loading-label') || 'Loading…';

    var sep = nextUrl.indexOf('?') === -1 ? '?' : '&';

    fetch(nextUrl + sep + 'section_id=' + sectionId)
      .then(function (r) { return r.text(); })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var grid = host.querySelector('[data-nga-cgrid]');
        var freshGrid = doc.querySelector('[data-nga-cgrid]');
        if (!grid || !freshGrid) throw new Error('grid missing');

        Array.prototype.slice.call(freshGrid.children).forEach(function (li) {
          // The editorial in-grid card belongs to page 1 only.
          if (li.hasAttribute('data-nga-grid-card')) return;
          grid.appendChild(li);
        });

        // Refresh the load-more control from the newly fetched page.
        var oldFoot = host.querySelector('[data-nga-loadmore-wrap]');
        var newFoot = doc.querySelector('[data-nga-loadmore-wrap]');
        if (oldFoot && newFoot) oldFoot.replaceWith(newFoot);
        else if (oldFoot) oldFoot.remove();

        window.history.replaceState({ nga: true }, '', nextUrl);
        document.dispatchEvent(new CustomEvent('nga:refresh', { detail: { root: grid } }));
      })
      .catch(function () {
        btn.disabled = false;
        btn.textContent = label;
        if (window.NGA && window.NGA.toast) window.NGA.toast('Could not load more products.');
      });
  });

  /* ---------------------------------------------------------------------
     Grid density
     --------------------------------------------------------------------- */
  function applyDensity() {
    var host = root();
    if (!host) return;
    var grid = host.querySelector('[data-nga-cgrid]');
    if (!grid) return;

    var d = null;
    try { d = localStorage.getItem(DENSITY_KEY); } catch (e) {}
    if (!d) d = grid.getAttribute('data-default-cols') || '3';

    grid.style.setProperty('--nga-cols', d);
    host.querySelectorAll('[data-nga-density]').forEach(function (b) {
      b.classList.toggle('is-active', b.getAttribute('data-nga-density') === d);
    });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-nga-density]');
    if (!btn) return;
    e.preventDefault();
    try { localStorage.setItem(DENSITY_KEY, btn.getAttribute('data-nga-density')); } catch (err) {}
    applyDensity();
  });

  /* ---------------------------------------------------------------------
     Mobile filter sheet
     --------------------------------------------------------------------- */
  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-nga-filter-open]')) {
      e.preventDefault();
      var facets = document.querySelector('.nga-facets');
      if (facets) facets.classList.toggle('is-mobile-open');
      return;
    }
    if (e.target.closest('[data-nga-filter-close]')) {
      var f = document.querySelector('.nga-facets');
      if (f) f.classList.remove('is-mobile-open');
    }
  });

  /* ---------------------------------------------------------------------
     Back / forward
     --------------------------------------------------------------------- */
  window.addEventListener('popstate', function () {
    if (root()) render(window.location.href, { replace: true, scroll: false });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applyDensity, { once: true });
  } else {
    applyDensity();
  }
  document.addEventListener('shopify:section:load', applyDensity);
})();

/* -------------------------------------------------------------------------
   Back to top, under the load-more meter
   ------------------------------------------------------------------------- */
document.addEventListener('click', function (e) {
  if (!e.target.closest('[data-nga-back-to-top]')) return;
  e.preventDefault();
  var grid = document.querySelector('[data-nga-cgrid-wrap]') || document.body;
  grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
});
